import { useCallback } from 'react'
import type {
  AuthServiceAccountListItem,
  AuthServiceAccountResponse,
  AuthRole,
} from '@ideon-api/sdk-internal'
import { parsedSdkErrorBody, useAuthServiceClient } from './sdk-client'
import { useSdkList, type ListState } from './use-sdk-list'

export type MachineUser = AuthServiceAccountListItem

export interface CreateResult {
  /** False on an idempotent re-create — the key already existed, and Auth0 cannot
   *  re-issue its secret, so `secret` is null. Never infer this from a null secret
   *  alone: the API tells us directly via `meta.created`. */
  created: boolean
  /** Present only on the original create. */
  secret: string | null
  /** The public half. Paired with the secret because config needs both, and it is the
   *  only moment they are available together. */
  clientId: string | null
  name: string
}

// Status-based, not message-based -- the SDK's per-status error classes are absent
// from its runtime exports, so `instanceof` throws.
export function isForbidden(error: unknown): boolean {
  return parsedSdkErrorBody(error).status === 403
}

// 403 is the one list failure that must never read as "no keys": the SPA shell has
// no server-side auth gate, so a non-ideon user loads the page and every section
// 403s. The API's body is the single word "forbidden" -- accurate and useless to
// read -- so the message is substituted in the fetcher, where the status is still
// available. Matching on the message downstream (useSdkList keeps only `message`)
// would break on an ALB HTML 403, on a body that gains a `message` key, and on the
// SDK's typed-error path.
export const FORBIDDEN_MESSAGE =
  'You do not have access to this account\u2019s machine users. This surface is Ideon-internal.'

export function useMachineUsers(slug: string): ListState<MachineUser> & { reload: () => void } {
  const getClient = useAuthServiceClient()
  // useSdkList takes a zero-arg fetcher and keys its "new list" reset on the
  // fetcher's identity, so this must be memoized per slug or every render
  // re-fetches and re-flashes the loading state.
  const fetcher = useCallback(
    () =>
      getClient()
        .then((client) => client.auth.listServiceAccounts(slug))
        .catch((error: unknown) => {
          if (isForbidden(error)) throw new Error(FORBIDDEN_MESSAGE)
          throw error
        }),
    [getClient, slug]
  )
  return useSdkList<MachineUser>(fetcher)
}


export function toCreateResult(
  response: AuthServiceAccountResponse,
  requestedName: string
): CreateResult {
  return {
    // meta.created, never `secret != null`: the API states outright whether it
    // minted a credential, and a create CAN come back with a null secret.
    created: response.meta?.created ?? false,
    secret: response.data?.auth0ClientSecret ?? null,
    clientId: response.data?.auth0ClientId ?? null,
    name: response.data?.name ?? requestedName,
  }
}

export function useCreateMachineUser(slug: string) {
  const getClient = useAuthServiceClient()

  return useCallback(
    async (name: string, role: AuthRole): Promise<CreateResult> => {
      const client = await getClient()
      const response: AuthServiceAccountResponse = await client.auth.createServiceAccount(slug, {
        name,
        role,
      })
      return toCreateResult(response, name)
    },
    [getClient, slug]
  )
}

export function useRevokeMachineUser(slug: string) {
  const getClient = useAuthServiceClient()

  return useCallback(
    async (id: string): Promise<void> => {
      const client = await getClient()
      await client.auth.revokeServiceAccount(slug, id)
    },
    [getClient, slug]
  )
}
